import Button from "@components/base/button";
import Loader from "@components/base/loader/Loader";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { countryDetails } from "@core/dictionary/global";
import { captureException } from "@sentry/react";
import ReactTableTrademo from "@shared/trademo-datatable/react-table-trademo";
import { getApiUrl, getClassifierApiUrl } from "@utils/baseUrl";
import commaFormat from "@utils/commaFormat";
import { getIntelUrl } from "@utils/intelUrl";
import axios from "axios";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import ReactCountryFlag from "react-country-flag";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import Layout from "../../components/layout/layout";
import { CLOUDINARY_INTEL_BASE_URL, ERROR_MSG } from "../../utils/enum";
import CompanyResultHeaders from "../company-search/components/companyResultHeaders";
import ProductSearchActions from "../product-search/components/ProductSearchActions";
import CompanySearchCard from "../company-search/components/companySearchCard";
import CompanySearchFilters from "../company-search/components/companySearchFilters";
import { companySearchTabs, datasetTypes, dateRangeOptions, getDefaultDateRange, initialMetrics, sortOptionsProduct } from "../company-search/enum";
import SubTabsBar from "./components/SubTabsBar";
import ProductAnalytics from "./components/ProductAnalytics";
import ProductTariffs from "./components/ProductTariffs";
import ProductAISummary from "./components/ProductAISummary";
import ProductSearchEmptyState from "./components/EmptyState";
import SaveSearchButton from "./components/EmptyState/SaveSearchButton";

const productSubTabKeys = {
  ANALYTICS: "analytics",
  TARIFFS: "tariffs",
  BUYER_SUPPLIER: "buyerSupplier",
  AI_SUMMARY: "aiSummary",
};

const subTabClickEventMap = {
  [productSubTabKeys.BUYER_SUPPLIER]: "newMay2026ProductSearchBuyerSupplierTabClick",
  [productSubTabKeys.ANALYTICS]: "newMay2026ProductSearchAnalyticsTabClick",
  [productSubTabKeys.TARIFFS]: "newMay2026ProductSearchTariffsTabClick",
  [productSubTabKeys.AI_SUMMARY]: "newMay2026ProductSearchAISummaryTabClick",
};

const avatarPalette = ["#fce7f3", "#fef3c7", "#dcfce7", "#dbeafe", "#ede9fe", "#fee2e2"];
const getAvatarBg = (name) => {
  const charCode = (name || "A").toUpperCase().charCodeAt(0);
  return avatarPalette[charCode % avatarPalette.length];
};

const SortDropdown = ({ sortField, onSortChange, onSortTrack }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={dropdownRef} className="tw-relative tw-inline-block">
      <div onClick={() => setOpen(!open)} className="tw-cursor-pointer tw-text-sm tw-font-medium tw-text-primary-purple-600 tw-flex tw-items-center tw-justify-between">
        <span className="material-symbols-outlined">swap_vert</span> Sort By: {sortField.label}
      </div>
      {open && (
        <div className="tw-text-sm tw-absolute tw-mt-1 tw-w-full tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-z-50">
          {sortOptionsProduct.map((opt) => (
            <div
              key={opt.value}
              onClick={() => {
                onSortChange(opt);
                onSortTrack?.();
                setOpen(false);
              }}
              className={`tw-px-3 tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100 ${sortField.value === opt.value ? "tw-bg-gray-200 tw-font-medium" : ""}`}>
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProductSearch = () => {
  const activeTab = companySearchTabs.PRODUCT;
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchedState, setSearchedState] = useState(null);
  const [rowPerPage, setRowPerPage] = useState(25);
  const [pageNumber, setPageNumber] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [sortField, setSortField] = useState(sortOptionsProduct[0]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelection, setClearSelection] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [datasetType, setDatasetType] = useState(datasetTypes.DIRECT);
  const [loading, setLoading] = useState(false);
  const [countryFilterOptions, setCountryFilterOptions] = useState([]);
  const [hsCodeFilterOptions, setHsCodeFilterOptions] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [countryFilterLoading, setCountryFilterLoading] = useState(false);
  const [hsCodeFilterLoading, setHsCodeFilterLoading] = useState(false);
  const filterFetched = useRef({ country: false, hsCode: false });
  const visitedTabsRef = useRef(new Set());
  const [activeSubTab, setActiveSubTab] = useState(productSubTabKeys.BUYER_SUPPLIER);
  const [activeTradingPartnersCell, setActiveTradingPartnersCell] = useState(null);
  const [activeTradingCountriesCell, setActiveTradingCountriesCell] = useState(null);
  const tradingPartnersRef = useRef(null);
  const tradingCountriesRef = useRef(null);
  const [suggestedHS, setSuggestedHS] = useState("");
  const [hsnClassifierLoading, setHsnClassifierLoading] = useState(false);
  const [hsnClassifierError, setHsnClassifierError] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const classifierHsCode = urlParams.get("hsCode") || "";
  const classifierTradeDirection = urlParams.get("tradeDirection") || "";
  const fromClassifier = urlParams.get("fromClassifier") === "true";
  const toTradeDirectionOption = (dir) => (dir === "import" ? { label: "Buyers of", value: "import" } : { label: "Suppliers of", value: "export" });
  const classifierInitialTradeDirection = fromClassifier ? toTradeDirectionOption(classifierTradeDirection) : undefined;
  const creditDetails = useSelector((state) => state.userDetails.creditDetails);
  const userFirstPlan = creditDetails?.data?.[0] ?? null;
  const currentUserPlan = userFirstPlan?.user_active_plan?.split("_").pop() || "";
  const defaultDateRange = useMemo(() => {
    if (currentUserPlan === "enterprise") return dateRangeOptions[2];
    return getDefaultDateRange(currentUserPlan);
  }, [currentUserPlan]);
  const STARTERPLAN = creditDetails?.data?.find((data) => data?.is_active && data?.user_active_plan?.toLowerCase()?.includes("starter"))?.navigate === "search";

  const initialFilters = useMemo(
    () => ({
      dateRange: defaultDateRange,
      listedCompany: null,
      jurisdiction: [],
      hsCode: [],
      metrics: initialMetrics,
    }),
    [defaultDateRange]
  );

  const [filters, setFilters] = useState(initialFilters);

  const effectiveDatasetType = searchedState?.datasetType ?? datasetType;
  const showAnalyticsTab = effectiveDatasetType === datasetTypes.DIRECT;

  useEffect(() => {
    if (!showAnalyticsTab && activeSubTab === productSubTabKeys.ANALYTICS) {
      setActiveSubTab(productSubTabKeys.BUYER_SUPPLIER);
    }
  }, [showAnalyticsTab, activeSubTab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tradingPartnersRef.current && !tradingPartnersRef.current.contains(event.target)) {
        setActiveTradingPartnersCell(null);
      }
      if (tradingCountriesRef.current && !tradingCountriesRef.current.contains(event.target)) {
        setActiveTradingCountriesCell(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const showDatasetToggle = useMemo(() => {
    if (!creditDetails?.data || !Array.isArray(creditDetails.data)) return false;
    const filteredDataset = creditDetails.data.filter((item) => item?.product_id !== "trademo_MC" && item.navigate !== "upgrade");
    const isMirrorSubscribed = filteredDataset.some((dataset) => dataset?.is_active && countryDetails[dataset?.product_id]?.name?.toLowerCase()?.includes("mirror"));
    const isDirectSubscribed = filteredDataset.some((dataset) => dataset?.is_active && !countryDetails[dataset?.product_id]?.name?.toLowerCase()?.includes("mirror"));
    return isDirectSubscribed && isMirrorSubscribed;
  }, [creditDetails]);

  const viewAllTradingPartners = async (row, options = {}) => {
    const companyId = row?.hashed_id;
    if (!companyId) return;
    try {
      const { data } = await axios.get(getApiUrl("companyProfiles/getSlug"), { params: { companyId } });
      if (data?.status === "success" && data?.data?.companySlug) {
        const urlParams = new URLSearchParams(window.location.search);
        const baseParams = new URLSearchParams({
          country: urlParams.get("country") || "multicountry",
          type: urlParams.get("type") || "import",
          tab: options?.tab || "I",
        });
        if (options?.subTab) {
          baseParams.set("subTab", options.subTab);
        }
        const url = getIntelUrl() + data.data.companySlug + "/" + data.data.aliasCompanyId + `?${baseParams.toString()}`;
        window.open(url, "_blank");
      } else {
        toast.error("No company profile found in our system !!!");
      }
    } catch (error) {
      captureException(error);
      toast.error(error?.response?.data?.message || "Unable to open company profile");
    }
  };

  const getCompanyProfileTabByType = () => {
    const normalizedType = searchedState?.tradeDirection?.value === "import" ? "importer" : "supplier";
    return normalizedType === "importer" ? "I" : "E";
  };

  const renderTradingPartnersCell = (row, openUpwards = false) => {
    const cacheKey = row.hashed_id;
    if (!cacheKey) return <span>-</span>;
    const partners = Array.isArray(row?.top_trading_partners) ? row.top_trading_partners : [];
    const totalCount = row?.trading_partners_count ?? partners.length;
    const isOpen = activeTradingPartnersCell === cacheKey;
    const countLabel = Number(totalCount).toLocaleString();
    return (
      <div className="tw-relative tw-flex tw-justify-end" ref={isOpen ? tradingPartnersRef : null}>
        {totalCount > 0 ? (
          <button
            className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[34px] tw-h-6 tw-bg-[#FEF3C7] tw-text-[#7A5D00] tw-rounded-full tw-px-2 tw-text-xs tw-font-semibold tw-border-0 hover:tw-bg-[#FDE68A]"
            onClick={(event) => {
              event.stopPropagation();
              setActiveTradingCountriesCell(null);
              setActiveTradingPartnersCell(isOpen ? null : cacheKey);
            }}>
            {countLabel}
          </button>
        ) : (
          <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[34px] tw-h-6 tw-bg-[#FEF3C7] tw-text-[#7A5D00] tw-rounded-full tw-px-2 tw-text-xs tw-font-semibold tw-border-0">0</span>
        )}
        {isOpen && (
          <div className={`tw-absolute tw-right-0 tw-z-40 tw-w-[420px] tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg ${openUpwards ? "tw-bottom-[calc(100%+10px)]" : "tw-top-[calc(100%+10px)]"}`}>
            {partners.length > 0 ? (
              <>
                <div className="tw-flex tw-flex-col tw-divide-y tw-divide-gray-200 tw-border-b tw-border-gray-200 tw-max-h-[280px] tw-overflow-y-auto">
                  {partners.map((partner) => {
                    const [displayName] = (partner.name || "").split("$$");
                    return (
                      <div key={partner.name} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-2 tw-py-2">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                          <span className="tw-inline-flex tw-shrink-0 tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-text-[10px] tw-font-semibold tw-text-[#6941C6]" style={{ backgroundColor: getAvatarBg(displayName) }}>
                            {(displayName || "").slice(0, 1).toUpperCase()}
                          </span>
                          <span className="tw-text-sm tw-text-gray-800 tw-truncate tw-capitalize">{displayName}</span>
                        </div>
                        {partner.doc_count ? (
                          <span className="tw-shrink-0 tw-text-sm tw-font-medium tw-text-gray-900">
                            {Number(partner.doc_count).toLocaleString()} <span className="tw-text-xs tw-font-normal">shipments</span>
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                {totalCount > 5 && (
                  <button
                    className="tw-mt-1 tw-px-3 tw-pb-2 tw-w-full tw-flex tw-items-center tw-gap-1 tw-text-left tw-text-sm tw-text-primary-purple-600 tw-border-0 tw-bg-transparent hover:tw-text-primary-purple-700"
                    onClick={(event) => {
                      event.stopPropagation();
                      setActiveTradingPartnersCell(null);
                      viewAllTradingPartners(row, {
                        tab: getCompanyProfileTabByType(),
                        subTab: "trading_partner",
                      });
                    }}>
                    <span className="material-symbols-outlined tw-text-base">open_in_new</span>
                    View All
                  </button>
                )}
              </>
            ) : (
              <div className="tw-p-2 tw-text-sm tw-text-gray-500">No trading partners found.</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTradingCountriesCell = (row, openUpwards = false) => {
    const cacheKey = row?.hashed_id;
    const countries = Array.isArray(row?.trading_countries) ? row.trading_countries : [];
    const countLabel = countries.length.toLocaleString();
    const isOpen = activeTradingCountriesCell === cacheKey;

    if (!cacheKey) return <span>-</span>;

    return (
      <div className="tw-relative tw-flex tw-justify-end" ref={isOpen ? tradingCountriesRef : null}>
        {countries.length > 0 ? (
          <button
            className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[34px] tw-h-6 tw-bg-[#FEF3C7] tw-text-[#7A5D00] tw-rounded-full tw-px-2 tw-text-xs tw-font-semibold tw-border-0 hover:tw-bg-[#FDE68A]"
            onClick={(event) => {
              event.stopPropagation();
              setActiveTradingPartnersCell(null);
              setActiveTradingCountriesCell(isOpen ? null : cacheKey);
            }}>
            {countLabel}
          </button>
        ) : (
          <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[34px] tw-h-6 tw-bg-[#FEF3C7] tw-text-[#7A5D00] tw-rounded-full tw-px-2 tw-text-xs tw-font-semibold tw-border-0">0</span>
        )}
        {isOpen && (
          <div className={`tw-absolute tw-right-0 tw-z-40 tw-w-[420px] tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg ${openUpwards ? "tw-bottom-[calc(100%+10px)]" : "tw-top-[calc(100%+10px)]"}`}>
            {countries.length > 0 ? (
              <div className="tw-flex tw-flex-col tw-divide-y tw-divide-gray-200 tw-max-h-[280px] tw-overflow-y-auto">
                {countries.map((country) => (
                  <div key={country.country} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-2 tw-py-2">
                    <ReactCountryFlag svg countryCode={country.country_code} /> <span className="tw-text-sm tw-text-gray-800 tw-capitalize tw-truncate">{country.country || "-"}</span>
                    {country.doc_count ? (
                      <span className="tw-shrink-0 tw-text-sm tw-font-medium tw-text-gray-900">
                        {Number(country.doc_count).toLocaleString()} <span className="tw-text-xs tw-font-normal">shipments</span>
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="tw-p-2 tw-text-sm tw-text-gray-500">No trading countries found.</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const tableColumns = useMemo(() => {
    const productColumns = [
      {
        name: <div className="tw-text-sm tw-font-semibold">S.No</div>,
        selector: (row) => row.id,
        miWidth: 60,
        isSticky: "start",
        left: "40px",
      },
      {
        name: <div className="tw-text-sm tw-font-semibold">Company Name & Address</div>,
        selector: (row) => (
          <div>
            <div className="tw-text-primary-purple-600 tw-underline tw-capitalize tw-text-sm tw-cursor-pointer" onClick={() => getSlugHandler(row.hashed_id)}>
              {row.key}
            </div>
            <div className="tw-mt-1 tw-text-xs tw-text-gray-600 tw-capitalize tw-line-clamp-3">{row.address}</div>
            <div className="tw-mt-1 tw-text-xs tw-text-gray-900 tw-capitalize">
              {`${row.state ? row.state + ", " : ""} ${row.country}`} <ReactCountryFlag svg countryCode={row.country_code} />
            </div>
          </div>
        ),
        miWidth: 250,
        isSticky: "start",
        left: "100px",
      },
      {
        name: <div className="tw-flex tw-justify-end tw-text-sm tw-font-semibold">Shipments</div>,
        selector: (row) => <span className="tw-flex tw-justify-end tw-font-ibm-plex-mono tw-text-gray-900 tw-text-sm tw-tracking-numbers">{commaFormat(row.doc_count || 0)}</span>,
        miWidth: "150px",
      },
      {
        name: <div className="tw-flex tw-justify-end tw-text-sm tw-font-semibold">Trading Partners</div>,
        selector: (row, index) => <div className="tw-flex tw-justify-end">{renderTradingPartnersCell(row, index % rowPerPage >= rowPerPage - 2 || index >= tableData.length - 2)}</div>,
        miWidth: "170px",
      },
      {
        name: <div className="tw-flex tw-justify-end tw-text-sm tw-font-semibold">Trading Countries</div>,
        selector: (row, index) => <div className="tw-flex tw-justify-end">{renderTradingCountriesCell(row, index % rowPerPage >= rowPerPage - 2 || index >= tableData.length - 2)}</div>,
        miWidth: "170px",
      },
      {
        name: <div className="tw-flex tw-justify-end tw-text-sm tw-font-semibold">Weight</div>,
        selector: (row) => <span className="tw-flex tw-justify-end tw-font-ibm-plex-mono tw-text-gray-900 tw-text-sm tw-tracking-numbers">{commaFormat(row.weight?.value || 0)}</span>,
        miWidth: "150px",
      },
      {
        name: <div className="tw-flex tw-justify-end tw-text-sm tw-font-semibold">Value (USD)</div>,
        selector: (row) => <span className="tw-flex tw-justify-end tw-font-ibm-plex-mono tw-text-gray-900 tw-text-sm tw-tracking-numbers">{commaFormat(row.shipment_value?.value || 0)}</span>,
        miWidth: "150px",
      },
      {
        name: <div className="tw-flex tw-justify-end tw-text-sm tw-font-semibold">Recently {searchedState?.tradeDirection?.value === "import" ? "Imported" : "Exported"} on</div>,
        selector: (row) => <span className="tw-flex tw-justify-end tw-text-gray-900 tw-text-sm tw-tracking-numbers">{row.recent_shipment_date ? moment(row.recent_shipment_date).format("MMM DD, YYYY") : "-"}</span>,
        miWidth: "150px",
      },
      {
        name: <div className="tw-flex tw-justify-center tw-text-sm tw-font-semibold">Actions</div>,
        selector: (row) => (
          <div className="tw-flex tw-justify-center">
            <ProductSearchActions rowData={row} setSelectedRowData={setSelectedRowData} getSlugHandler={getSlugHandler} isMirror={datasetType === datasetTypes.MIRROR} handleViewClick={handleViewClick} tradeDirection={searchedState.tradeDirection.value} onTrackViewContacts={() => trademoTrackingEvents(trackingEventDictionary.newMay2026ProductSearchViewContacts, generateTrackingObject("product-search", window.location.pathname, currentUserPlan))} onTrackBookmark={() => trademoTrackingEvents(trackingEventDictionary.newMay2026ProductSearchBookmarked, generateTrackingObject("product-search", window.location.pathname, currentUserPlan))} />
          </div>
        ),
        miWidth: "80px",
      },
    ];

    return productColumns;
  }, [searchedState, filters, activeTradingPartnersCell, activeTradingCountriesCell, rowPerPage, datasetType, tableData]);

  const handleTrademoTableSearchPerRowsChange = (data) => {
    setRowPerPage(data);
  };

  const handleSelectedRowsChange = (rows) => {
    setSelectedRows(rows || []);
  };

  const updateTrademoTableObject = (data) => {
    setRowPerPage(data.perPage);
    setPageNumber(data.currentPage + 1);
  };

  const handleTrademoTablePageChange = (page, perPage) => {
    setRowPerPage(perPage);
    setPageNumber(page);
    if (page * perPage >= tableData.length - rowPerPage && tableData.length < totalDataCount) {
      intelSearchMultiCountry(true);
    }
  };

  const getSlugHandler = async (companyId, P = "") => {
    try {
      const params = {
        companyId: companyId,
      };
      const { data } = await axios.get(getApiUrl("companyProfiles/getSlug"), { params });
      if (data?.status === "success" && data?.data?.companySlug) {
        trademoTrackingEvents(trackingEventDictionary.newMay2026ProductSearchCompanyProfileClick, generateTrackingObject("product-search", window.location.pathname, currentUserPlan));
        const url = getIntelUrl() + data.data.companySlug + "/" + data.data.aliasCompanyId + `?country=${urlParams.get("country") || "multicountry"}&type=${searchedState?.tradeDirection?.value || urlParams.get("type") || "import"}&tab=I${P || ""}`;
        window.open(url, "_blank");
      } else {
        toast.error("No company profile found in our system !!!");
      }
    } catch (err) {
      captureException(err);
      toast.error(ERROR_MSG);
    }
  };

  const handleViewClick = (row) => {
    trademoTrackingEvents(trackingEventDictionary.newMay2026ProductSearchViewShipments, generateTrackingObject("product-search", window.location.pathname, currentUserPlan));
    const searchCategory = searchedState?.search?.[0]?.category;
    const currentKey = searchCategory === "HS Code" ? "product_hscode" : searchCategory === "Product" ? "product_description" : "product_description";
    const searchedItem = (searchedState?.search || [])
      .map((s) => s?.label)
      .filter(Boolean)
      .join("||");
    const params = {
      redirectingFrom: "company",
      redirectingProfile: searchedState?.tradeDirection?.value === "import" ? "consignee" : "shipper",
      redirectingProfileIds: row.hashed_id,
      profileName: row.slug || "",
      profileId: row.entity_id,
      searchedItem,
      currentKey,
      matchType: "contains",
      fromDate: filters?.dateRange?.startDate || "",
      toDate: filters?.dateRange?.endDate || "",
    };
    sessionStorage.removeItem("selectedCountry");
    window.open(getIntelUrl() + `all-country-shipments?${new URLSearchParams(params).toString()}`, "_blank");
  };

  useEffect(() => {
    if (!clearSelection) return undefined;
    const timer = setTimeout(() => setClearSelection(false), 0);
    return () => clearTimeout(timer);
  }, [clearSelection]);

  const clearSelections = () => {
    setClearSelection(true);
  };

  const handleDatasetTypeChange = (e) => {
    const updatedDataset = e.target.checked ? datasetTypes.MIRROR : datasetTypes.DIRECT;
    setDatasetType(updatedDataset);
    resetSearch();
    trademoTrackingEvents(trackingEventDictionary.newMay2026ProductSearchDatasetToggle, { ...generateTrackingObject("product-search", window.location.pathname, currentUserPlan), datasetType: updatedDataset });
  };

  const buildActiveFiltersPayload = () => {
    const payload = {
      uniqueImportExporterShipment: filters.metrics.NumberofShipments.from && filters.metrics.NumberofShipments.to ? [+filters.metrics.NumberofShipments.from, +filters.metrics.NumberofShipments.to] : [],
      uniqueImportExporterWeight: filters.metrics.TotalWeight.from && filters.metrics.TotalWeight.to ? [+filters.metrics.TotalWeight.from, +filters.metrics.TotalWeight.to] : [],
      uniqueImportExporterValue: filters.metrics.TotalTradeValue.from && filters.metrics.TotalTradeValue.to ? [+filters.metrics.TotalTradeValue.from, +filters.metrics.TotalTradeValue.to] : [],
    };
    if (activeTab === companySearchTabs.PRODUCT && filters.jurisdiction?.length > 0) {
      if (searchedState?.tradeDirection?.value === "import") {
        payload.inboundCountry = filters.jurisdiction.map((item) => item.toLowerCase());
      } else {
        payload.outboundCountry = filters.jurisdiction.map((item) => item.toLowerCase());
      }
    }
    if (filters.hsCode?.length > 0) payload.sixDigitHsCode = filters.hsCode;
    return payload;
  };

  const intelSearchMultiCountry = async (pagination = false) => {
    try {
      if (!pagination) {
        setPageNumber(1);
        setTableData([]);
      }
      setLoading(true);

      const payload = {
        tab: "company",
        isFilterDataIncludes: false,
        data_type: searchedState.datasetType,
        company_tab_trade_direction: "both",
        from: pagination ? tableData.length : 0,
        sort: sortField.value,
        search_type: "basic",
        order: "desc",
        companyType: searchedState?.tradeDirection?.value === "import" ? "consignee" : "shipper",
        fromDate: filters.dateRange.startDate,
        toDate: filters.dateRange.endDate,
        search: searchedState.search.map((item) => item.label).join("||"),
        key: searchedState.search[0].category === "HS Code" ? "product_hscode" : searchedState.search[0].category === "Product" ? "product_description" : "product_and_hscode",
        queryType: "exact",
        must_not: pagination ? tableData.map((item) => `${item.key}$$${item.hashed_id}`) : [],
        search_source: "product_search",
        ...buildActiveFiltersPayload(),
      };

      const { data } = await axios.post(getApiUrl("intelSearchMultiCountry"), payload);
      const newData = data?.data || [];
      const finalData = pagination ? [...tableData, ...newData] : newData;

      setTableData(finalData);
      setTotalDataCount(data?.importerCount || data?.exporterCount || 0);
    } catch (err) {
      captureException(err);
      toast.error(ERROR_MSG);
    } finally {
      setLoading(false);
    }
  };

  const getCountryFilter = async () => {
    setCountryFilterLoading(true);
    try {
      const isImport = searchedState?.tradeDirection?.value === "import";
      const regionKey = isImport ? "inboundCountryRegion" : "outboundCountryRegion";
      const countryKey = isImport ? "inboundCountry" : "outboundCountry";
      let payload = {
        from: 0,
        sort: "date",
        search_type: "basic",
        order: "desc",
        companyType: "",
        companyDirection: isImport ? "consignee" : "shipper",
        analyticsField: "",
        fromDate: filters.dateRange.startDate,
        toDate: filters.dateRange.endDate,
        search: searchedState.search.map((item) => item.label).join("||"),
        key: searchedState.search[0].category === "HS Code" ? "product_hscode" : searchedState.search[0].category === "Product" ? "product_description" : "product_and_hscode",
        queryType: "exact",
        is_chemical: false,
        isFilterDataIncludes: true,
        ...buildActiveFiltersPayload(),
        [regionKey]: [],
        [countryKey]: [],
        opened_filter: [regionKey, countryKey],
        tab: "company",
        data_type: searchedState.datasetType,
        company_tab_trade_direction: "both",
      };

      const { data } = await axios.post(getApiUrl("intelSearchMultiCountry"), payload);
      const flatCountries = [];
      data?.[regionKey]?.forEach((item) => {
        item.region?.forEach((country) => {
          flatCountries.push(country);
        });
      });
      setCountryFilterOptions(flatCountries.sort((a, b) => a.key.localeCompare(b.key)));
      filterFetched.current.country = true;
    } catch (err) {
      captureException(err);
      toast.error(ERROR_MSG);
    } finally {
      setCountryFilterLoading(false);
    }
  };

  const getHsCodeFilterOptions = async () => {
    setHsCodeFilterLoading(true);
    try {
      const payload = {
        from: 0,
        sort: "date",
        search_type: "basic",
        order: "desc",
        companyType: "",
        companyDirection: searchedState?.tradeDirection?.value === "import" ? "consignee" : "shipper",
        analyticsField: false,
        fromDate: filters.dateRange.startDate,
        toDate: filters.dateRange.endDate,
        search: searchedState.search.map((item) => item.label).join("||"),
        key: searchedState.search[0].category === "HS Code" ? "product_hscode" : searchedState.search[0].category === "Product" ? "product_description" : "product_and_hscode",
        queryType: "exact",
        is_chemical: false,
        isFilterDataIncludes: true,
        ...buildActiveFiltersPayload(),
        sixDigitHsCode: [],
        opened_filter: ["sixDigitHsCode"],
        tab: "company",
        data_type: searchedState.datasetType,
        company_tab_trade_direction: "both",
      };
      const { data } = await axios.post(getApiUrl("intelSearchMultiCountry"), payload);
      setHsCodeFilterOptions((data?.sixDigitHsCode || []).sort((a, b) => String(a?.key || "").localeCompare(String(b?.key || ""))));
      filterFetched.current.hsCode = true;
    } catch (err) {
      captureException(err);
      toast.error(ERROR_MSG);
    } finally {
      setHsCodeFilterLoading(false);
    }
  };

  const fetchCountryFilterIfNeeded = () => {
    if (!filterFetched.current.country && !countryFilterLoading) {
      getCountryFilter();
    }
  };

  const fetchHsCodeFilterIfNeeded = () => {
    if (searchedState?.search?.[0]?.category !== "HS Code" && !filterFetched.current.hsCode && !hsCodeFilterLoading) {
      getHsCodeFilterOptions();
    }
  };

  useEffect(() => {
    filterFetched.current.hsCode = false;
  }, [filters.jurisdiction]);

  useEffect(() => {
    filterFetched.current.country = false;
  }, [filters.hsCode]);

  useEffect(() => {
    setSelectedRows([]);
  }, [searchedState]);

  useEffect(() => {
    if (searchedState) {
      intelSearchMultiCountry();
    }
  }, [searchedState, filters, sortField]);

  const handleSearchClick = (searchValues, tradeDirection, options = {}) => {
    const { datasetType: datasetTypeOverride, filters: filtersOverride } = options;
    setSelectedRows([]);
    setSelectedRowData(null);
    setFilters(filtersOverride || initialFilters);
    setSortField(sortOptionsProduct[0]);
    setTableData([]);
    setTotalDataCount(0);
    setPageNumber(1);
    setCountryFilterOptions([]);
    setHsCodeFilterOptions([]);
    filterFetched.current = { country: false, hsCode: false };
    visitedTabsRef.current = new Set();
    setActiveSubTab(productSubTabKeys.BUYER_SUPPLIER);
    setSuggestedHS("");
    setHsnClassifierLoading(false);
    setHsnClassifierError(false);
    if (searchValues && searchValues.length > 0) {
      if (searchValues[0].category === "HS Code") {
        setSuggestedHS(searchValues[0].label);
      } else {
        setHsnClassifierLoading(true);
        fetchSuggestedHS(searchValues);
      }
    }
    const effectiveDatasetType = datasetTypeOverride || datasetType;
    if (datasetTypeOverride && datasetTypeOverride !== datasetType) setDatasetType(datasetTypeOverride);
    setSearchedState({ search: searchValues, tradeDirection, datasetType: effectiveDatasetType });
    const isImporterSearch = tradeDirection?.value === "import";
    const isMirrorDataset = effectiveDatasetType === datasetTypes.MIRROR;
    const datasetEvent = isImporterSearch ? (isMirrorDataset ? trackingEventDictionary.newMay2026ProductSearchBuyersOfMirrorDataset : trackingEventDictionary.newMay2026ProductSearchBuyersOfDirectDataset) : isMirrorDataset ? trackingEventDictionary.newMay2026ProductSearchSuppliersOfMirrorDataset : trackingEventDictionary.newMay2026ProductSearchSuppliersOfDirectDataset;
    trademoTrackingEvents(datasetEvent, generateTrackingObject("product-search", window.location.pathname, currentUserPlan));
  };

  useEffect(() => {
    if (!fromClassifier || !classifierHsCode || !classifierTradeDirection) return;

    const searchValues = [{ category: "HS Code", label: classifierHsCode, value: classifierHsCode }];
    const tradeDirection = toTradeDirectionOption(classifierTradeDirection);

    setSelectedItems(searchValues);
    setSearchInput(classifierHsCode);
    handleSearchClick(searchValues, tradeDirection);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSuggestedHSClick = () => {
    const hsSearchValue = [{ category: "HS Code", label: suggestedHS, value: suggestedHS }];
    setSelectedItems(hsSearchValue);
    setSearchInput(suggestedHS);
    handleSearchClick(hsSearchValue, searchedState?.tradeDirection);
  };

  const reloadRecentSearch = (row) => {
    const category = row.category_name === "HS Code" ? "HS Code" : "Product";
    const searchValues = (row.search || []).filter(Boolean).map((term) => ({ category, label: term, value: term }));
    if (!searchValues.length) {
      toast.error("Unable to reload this search");
      return;
    }
    const tradeDirection = row.company_type === "shipper" ? { label: "Suppliers of", value: "export" } : { label: "Buyers of", value: "import" };
    setSelectedItems(searchValues);
    setSearchInput(searchValues.map((item) => item.label).join(", "));
    handleSearchClick(searchValues, tradeDirection, { datasetType: row.dataset_type === datasetTypes.MIRROR ? datasetTypes.MIRROR : datasetTypes.DIRECT });
  };

  const reloadSavedSearch = (row) => {
    const searchData = row.search_data;
    if (!searchData?.search?.length) {
      toast.error("Unable to reload this saved search");
      return;
    }
    setSelectedItems(searchData.search);
    setSearchInput(searchData.search.map((item) => item.label).join(", "));
    handleSearchClick(searchData.search, searchData.tradeDirection, { datasetType: searchData.datasetType, filters: searchData.filters });
  };

  const fetchSuggestedHS = async (searchValues) => {
    try {
      const payload = {
        productTitle: searchValues[0].label,
        till_six: true,
        multiple_hs: true,
      };
      const { data } = await axios.post(getClassifierApiUrl("hsn_classifier"), payload);
      setSuggestedHS(data?.suggestedHS || "");
    } catch (err) {
      setSuggestedHS("");
      setHsnClassifierError(true);
    } finally {
      setHsnClassifierLoading(false);
    }
  };

  const resetSearch = () => {
    trademoTrackingEvents(trackingEventDictionary.newMay2026ProductSearchReset, generateTrackingObject("product-search", window.location.pathname, currentUserPlan));
    setTableData([]);
    setSearchedState(null);
    setTotalDataCount(0);
    setSelectedRows([]);
    setSelectedRowData(null);
    setPageNumber(1);
    setSelectedItems([]);
    setSearchInput("");
    setSortField(sortOptionsProduct[0]);
    setFilters(initialFilters);
    setCountryFilterOptions([]);
    setHsCodeFilterOptions([]);
    filterFetched.current = { country: false, hsCode: false };
    visitedTabsRef.current = new Set();
    setActiveSubTab(productSubTabKeys.BUYER_SUPPLIER);
    setSuggestedHS("");
    setHsnClassifierError(false);
  };

  const handleSubTabChange = (key) => {
    visitedTabsRef.current.add(key);
    setActiveSubTab(key);
    const eventKey = subTabClickEventMap[key];
    if (eventKey) {
      trademoTrackingEvents(trackingEventDictionary[eventKey], generateTrackingObject("product-search", window.location.pathname, currentUserPlan));
    }
  };

  const renderSuppliersBody = (hasActiveFilters) => {
    if (loading && tableData.length === 0) {
      return null;
    }

    if (tableData.length === 0 && !loading && !hasActiveFilters) {
      return (
        <div className="tw-text-[15px] tw-gap-3 tw-max-w-72 tw-mx-auto tw-mt-12 tw-leading-[1.43] tw-flex tw-justify-center tw-flex-col">
          <img src={`${CLOUDINARY_INTEL_BASE_URL}/v1759739973/intel/misc/empty_state_nuhcpl.svg`} alt="no-results" className="tw-mx-auto" />
          <div className="tw-text-center tw-mt-4 tw-font-medium">No results found. Try checking the spelling, using fewer words, or different keywords.</div>
        </div>
      );
    }

    return (
      <>
        <div className="tw-flex tw-justify-between tw-items-start tw-pt-4 tw-gap-4">
          <div className="tw-flex-1 tw-min-w-0">
            <div className="tw-text-sm tw-font-semibold tw-flex tw-items-center tw-gap-1 tw-flex-wrap">
              <span className="tw-whitespace-nowrap">{`List of ${searchedState.tradeDirection.value === "import" ? "importers for" : "exporters for"}`}</span>
              {searchedState.search.map((s) => (
                <div key={s.label} className="tw-border tw-text-gray-500 tw-border-gray-200 tw-bg-white tw-px-2 tw-py-0.5 tw-rounded-full tw-shadow-sm tw-whitespace-nowrap">
                  {Number.isInteger(+s.label) ? "HS : " + s.label : s.label}
                </div>
              ))}
            </div>
          </div>

          {tableData.length > 0 && (
            <div className="tw-flex-shrink-0">
              <SortDropdown sortField={sortField} onSortChange={(sort) => setSortField(sort)} onSortTrack={() => trademoTrackingEvents(trackingEventDictionary.newMay2026ProductSearchSortChange, generateTrackingObject("product-search", window.location.pathname, currentUserPlan))} />
            </div>
          )}
        </div>

        <div className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-mt-3 tw-gap-4">
          {tableData.length > 0 ? <CompanyResultHeaders selectedRows={selectedRows} setSelectedRows={setSelectedRows} totalDataCount={totalDataCount} clearSelections={clearSelections} singleRow={selectedRowData} setSingleRow={(val) => setSelectedRowData(val)} filters={filters} searchedState={searchedState} sortField={sortField} tableData={tableData} activeTab={activeTab} source="product_search" /> : <div />}
          {selectedRows.length === 0 ? <CompanySearchFilters activeTab={activeTab} tradeDirection={searchedState?.tradeDirection?.value} filters={filters} setFilters={setFilters} tableData={tableData} initialFilters={initialFilters} currentUserPlan={currentUserPlan} countryFilterOptions={countryFilterOptions} countryFilterLoading={countryFilterLoading} hsCodeFilterOptions={hsCodeFilterOptions} hsCodeFilterLoading={hsCodeFilterLoading} onJurisdictionFilterOpen={fetchCountryFilterIfNeeded} onHsCodeFilterOpen={fetchHsCodeFilterIfNeeded} hasActiveFilters={hasActiveFilters} hideHsCodeFilter={searchedState?.search?.[0]?.category === "HS Code"} trackingSource="product-search" /> : <div />}
        </div>

        {tableData.length > 0 ? (
          <div className="tw-border tw-border-gray-200 tw-rounded-xl tw-overflow-hidden tw-bg-white tw-mt-4 tw-shadow-sm">
            <ReactTableTrademo columns={tableColumns} data={tableData} paginationTotalRows={totalDataCount} onSelectedRowsChange={handleSelectedRowsChange} paginationPerPage={rowPerPage} paginationDefaultPage={pageNumber} selectedData={selectedRows} onChangeRowsPerPage={(e) => handleTrademoTableSearchPerRowsChange(e)} onTableObjectChange={(e) => updateTrademoTableObject(e)} onChangePage={handleTrademoTablePageChange} clearSelectedRows={clearSelection} hideSorting={true} options={[25, 50, 100]} />
          </div>
        ) : (
          <div className="tw-text-[15px] tw-gap-3 tw-border tw-border-gray-200 tw-rounded-lg tw-mx-auto tw-mt-4 tw-h-[calc(100vh-305px)] tw-leading-[1.43] tw-flex tw-justify-center tw-flex-col tw-bg-white">
            <img src={`${CLOUDINARY_INTEL_BASE_URL}/v1759739973/intel/misc/empty_state_nuhcpl.svg`} alt="no-results" className="tw-mx-auto" />
            <div className="tw-text-center tw-mt-4 tw-font-medium">No results found for the applied filters.</div>
          </div>
        )}
      </>
    );
  };

  const renderContent = () => {
    const hasActiveFilters = filters.dateRange.label !== initialFilters.dateRange.label || filters.jurisdiction.length > 0 || filters.hsCode.length > 0 || Object.values(filters.metrics).some((m) => m.from !== "" || m.to !== "");

    if (!searchedState) {
      return <ProductSearchEmptyState onReloadRecent={reloadRecentSearch} onReloadSaved={reloadSavedSearch} />;
    }

    return (
      <div className="tw-border-t tw-border-gray-200 tw-bg-white tw-px-5 tw-min-h-[calc(100vh-180px)] tw-pb-6">
        <div>
          {suggestedHS && searchedState?.search?.[0]?.category !== "HS Code" && (
            <div className="tw-text-sm tw-bg-primary-purple-50 tw-my-4 tw-w-fit tw-mx-auto tw-text-gray-900 tw-px-3 tw-py-2 tw-rounded-lg">
              Looking to refine results? Search using HS Code:{" "}
              <button onClick={handleSuggestedHSClick} className="tw-font-semibold tw-text-primary-purple-600 tw-underline tw-underline-offset-2 tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer hover:tw-text-primary-purple-800">
                {suggestedHS}
              </button>
            </div>
          )}
          <SubTabsBar activeTab={activeSubTab} onChange={handleSubTabChange} tradeDirection={searchedState?.tradeDirection?.value} showAnalyticsTab={showAnalyticsTab} importerCount={totalDataCount} exporterCount={totalDataCount} actions={<SaveSearchButton searchedState={searchedState} filters={filters} />} />
        </div>

        {activeSubTab === productSubTabKeys.BUYER_SUPPLIER && renderSuppliersBody(hasActiveFilters)}
        {showAnalyticsTab && (activeSubTab === productSubTabKeys.ANALYTICS || visitedTabsRef.current.has(productSubTabKeys.ANALYTICS)) && (
          <div style={{ display: activeSubTab === productSubTabKeys.ANALYTICS ? undefined : "none" }}>
            <ProductAnalytics searchedState={searchedState} filters={filters} currentUserPlan={currentUserPlan} isActive={activeSubTab === productSubTabKeys.ANALYTICS} />
          </div>
        )}
        {(activeSubTab === productSubTabKeys.TARIFFS || visitedTabsRef.current.has(productSubTabKeys.TARIFFS)) && (
          <div style={{ display: activeSubTab === productSubTabKeys.TARIFFS ? undefined : "none" }}>
            <ProductTariffs searchedState={searchedState} suggestedHS={suggestedHS} hsnClassifierLoading={hsnClassifierLoading} hsnClassifierError={hsnClassifierError} defaultDateRange={defaultDateRange} currentUserPlan={currentUserPlan} />
          </div>
        )}
        {(activeSubTab === productSubTabKeys.AI_SUMMARY || visitedTabsRef.current.has(productSubTabKeys.AI_SUMMARY)) && (
          <div style={{ display: activeSubTab === productSubTabKeys.AI_SUMMARY ? undefined : "none" }}>
            <ProductAISummary searchedState={searchedState} defaultDateRange={defaultDateRange} />
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout type={"productSearch"} pageTitle="Find Buyers & Suppliers | Trademo Intel" setNotification={() => null}>
      {loading && <Loader />}
      {!STARTERPLAN && (
        <div className="tw-text-gray-900 tw-mx-auto">
          <div className="tw-flex tw-items-end tw-w-full tw-justify-center tw-gap-3 tw-pb-3 tw-pt-4">
            <div>
              {showDatasetToggle && (
                <div className="tw-flex tw-justify-end tw-px-8 tw-text-sm">
                  <div className="tw-flex tw-items-center tw-font-medium tw-gap-1">
                    <span className={`${datasetType === datasetTypes.DIRECT ? "tw-text-gray-900" : "tw-text-gray-400"} tw-pr-2`}>Direct Datasets</span>
                    <Form.Check type="switch" id="data-set-switch" checked={datasetType === datasetTypes.MIRROR} onChange={handleDatasetTypeChange} className="tw-w-fit !tw-cursor-pointer [&_input:checked+label::before]:!tw-bg-primary-purple-500 [&_input:checked+label::before]:!tw-border-primary-purple-500" />
                    <span className={`${datasetType === datasetTypes.MIRROR ? "tw-text-gray-900" : "tw-text-gray-400"}`}>Mirror Datasets</span>
                  </div>
                </div>
              )}
              <CompanySearchCard activeTab={activeTab} onSearch={handleSearchClick} setSelectedItems={setSelectedItems} selectedItems={selectedItems} searchInput={searchInput} setSearchInput={setSearchInput} resetSearch={resetSearch} initialTradeDirection={classifierInitialTradeDirection} searchedTradeDirection={searchedState?.tradeDirection} onReloadRecentSearch={reloadRecentSearch} onTrackTradeDirectionChange={(dir) => trademoTrackingEvents(trackingEventDictionary.newMay2026ProductSearchTradeDirectionChanged, { ...generateTrackingObject("product-search", window.location.pathname, currentUserPlan), tradeDirection: dir?.value })} />
            </div>

            {searchedState && (
              <div data-for="reset" className="tw-mb-4" data-tip="Clear all searches and filters">
                <Button variant={"secondaryWithIcon"} size={"medium"} className="tw-rounded-[20px]" onClick={resetSearch}>
                  <span className="material-symbols-outlined">find_replace</span>
                  <span className="tw-text-sm tw-font-medium">Reset</span>
                </Button>
                <ReactTooltip className="trademo-tooltip" textColor="#FFFFFF" place="bottom" id="reset" />
              </div>
            )}
          </div>

          {renderContent()}
        </div>
      )}
    </Layout>
  );
};

export default ProductSearch;
